import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Autocomplete, MenuItem } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import { getHubName, getVehicleGroupsByCity } from "../../api/api";
import commonapihandler from "../../common/commonapihandler";
import toast from "react-hot-toast";

const groupSchema = z.object({
  city: z.any().optional(),
  hub: z.any().optional(),

  name: z
    .union([
      z.string().min(1, "Group name is required"),
      z.object({
        id: z.number(),
        groupName: z.string(),
        groupCode: z.string(),
        securityDeposit : z.number()
      }),
    ])
    .nullable(),

  code: z.string().min(1, "Group code is required"),
  securityDeposit: z.any(),
});

export default function Mygroup({
  myGroups,
  selectedRow,
  vehicleProfileData,
  getVehicleProfileGroupsAPI,
  setGroupsList,
  groupsList,
  getGroupsAPI,
  getVehicleByLicensePlateAPI
}) {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const cities = useSelector((state) => state?.cities?.citiesData);

  const [hubs, setHubs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const hasGroup = myGroups?.id ? true : false;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      code: "",
      name: null,
      securityDeposit: "",
      city: null,
      hub: null,
    },
  });

  const values = watch();

  const getHubAPI = async (cityId) => {
    const apiDetails = await getHubName(cityId);
    const data = await commonapihandler(apiDetails?.apiUrl, apiDetails?.method);
    setHubs(data?.entities?.rows || []);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  const addGroupAgainstVehicle = async (data) => {
    let payload = {
      id: selectedRow?.id,
      vehicleGroupId: data?.id,
      vehicleGroupText: data?.name,
    };

    try {
      const response = await fetch(`${apiUrl}/api/vehicles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      const res = await response.json();

      if (res?.statusCode === "0100") {
        toast.success(hasGroup ? "Group updated" : "Group added");
        reset();
        await getVehicleByLicensePlateAPI(selectedRow?.registrationno);
      } else {
        toast.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const onSubmit = async (data) => {
    const isExistingGroup = typeof data?.name === "object" && data?.name?.id;

    const payload = {
      name: isExistingGroup ? data.name.groupName : data.name,
      code: data?.code,
      cityId: vehicleProfileData[0]?.cityId,
      securityDeposit: data?.securityDeposit,
    };

    // ONLY update when dropdown group selected
    if (isExistingGroup) {
      payload.id = data.name.id;
    }

    try {
      const response = await fetch(`${apiUrl}/api/vehiclegroups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      const res = await response.json();

      if (res?.statusCode === "0100") {
        toast.success(isExistingGroup ? "Group updated" : "New group created");

        await addGroupAgainstVehicle(res?.dataValues);
        setIsEditing(false);
      } else {
        toast.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const columns = [
    { field: "id", headerName: "Id", flex: 1 },
    { field: "code", headerName: "Group Code", flex: 1 },
    { field: "name", headerName: "Group Name", flex: 1.5 },
    { field: "securityDeposit", headerName: "Security Deposit", flex: 1 },
    { field: "createdAt", headerName: "Created On", flex: 1 },
  ];

  const groupFields = [
    {
      label: "Group Name",
      value: "name",
      type: "autocomplete",
      subCategory: groupsList.map((item) => ({
        label: item.name,
        value: {
          id: item.id,
          groupName: item.name,
          groupCode: item.code,
          securityDeposit : item?.securityDeposit
        },
      })),
    },
    {
      label: "Group Code",
      value: "code",
      type: "text",
    },
    {
      label: "Security Deposit",
      value: "securityDeposit",
      type: "number",
    },
  ];

  const renderField = (field) => {
    if (field.type === "autocomplete") {
      return (
        <Controller
          name={field.value}
          control={control}
          render={({ field: rhf }) => (
            <Autocomplete
              freeSolo
              options={field.subCategory || []}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option.label || "";
              }}
              value={
                typeof rhf.value === "string"
                  ? rhf.value
                  : field.subCategory?.find(
                      (opt) => opt.value?.id === rhf.value?.id,
                    ) || null
              }
              onChange={(_, value) => {
                if (typeof value === "string") {
                  // typed manually
                  rhf.onChange(value);
                  setValue("code", ""); // clear group code
                  setValue("securityDeposit", ""); // clear group code
                } else {
                  // selected from dropdown
                  rhf.onChange(value?.value || "");
                  setValue("code", value?.value?.groupCode || "");
                  setValue("securityDeposit", value?.value?.securityDeposit || "");
                }
              }}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  rhf.onChange(value);
                  setValue("code", ""); // typing = no group code
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!errors[field.value]}
                  helperText={errors[field.value]?.message}
                  placeholder="Search or type vehicle number"
                />
              )}
            />
          )}
        />
      );
    }

    return (
      <Controller
        name={field.value}
        control={control}
        render={({ field: rhf }) => (
          <TextField
            {...rhf}
            type={field.type}
            size="small"
            fullWidth
            error={!!errors[field.value]}
            helperText={errors[field.value]?.message}
          />
        )}
      />
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between">
        <div className="flex gap-2"><div className="h-5 w-1 rounded bg-teal-600"></div>
        <h2 className="text-lg font-semibold text-slate-800">My Group</h2></div>
        {!isEditing && hasGroup && (
          <Button variant="contained" onClick={handleEdit}>
            Edit
          </Button>
        )}
      </div>

      {hasGroup && !isEditing && (
        <>
          <Box sx={{ height: 100, background: "#fff", marginTop : 4 }}>
            <DataGrid
              rows={[
                {
                  id: myGroups?.id,
                  code: myGroups?.code,
                  name: myGroups?.name,
                  securityDeposit: myGroups?.securityDeposit,
                  createdAt: myGroups?.createdAt,
                },
              ]}
              columns={columns}
              hideFooter
            />
          </Box>
        </>
      )}

      {(isEditing || !hasGroup) && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {groupFields.map((field, i) => (
              <div key={i}>
                <label className="block mb-1 text-sm font-semibold">
                  {field.label}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button type="submit" variant="contained">
              Save
            </Button>

            {hasGroup && (
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </Box>
        </form>
      )}
    </div>
  );
}
